import { useEffect, useState, type CSSProperties } from 'react';
import { C2Button } from '../c2';

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 14,
  border: '1px solid var(--color-hairline-strong)',
  background: 'var(--color-surface)',
  fontSize: 13,
  fontFamily: 'inherit',
  color: 'var(--color-ink)',
  outline: 'none',
  boxSizing: 'border-box',
};

const copyBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '2px 6px',
  borderRadius: 4,
  color: 'var(--color-ink-soft)',
  fontSize: 14,
};

const EXAMPLES = [
  {
    url: 'https://acme.notion.site/Q4-2026-Roadmap-a847b92c',
    slug: 'roadmap',
    title: 'Roadmap',
    hint: 'A long Notion URL',
  },
  {
    url: 'https://figma.com/file/9j3nK2x/acme-design-system?node-id=423',
    slug: 'design-system',
    title: 'Design System',
    hint: 'A Figma file',
  },
  {
    url: 'https://docs.google.com/document/d/1Bf_OgC-9k2mXuZxE3nP/edit',
    slug: 'expense-policy',
    title: 'Expense Policy',
    hint: 'A Google Doc',
  },
];

function suggestSlug(url: string): string {
  if (!url) return '';
  try {
    const u = new URL(url.startsWith('http') ? url : 'https://' + url);
    const host = u.hostname.replace(/^www\./, '').split('.')[0] ?? '';
    const pathSeg = u.pathname.split('/').filter(Boolean).slice(-1)[0] ?? '';
    const clean = (pathSeg || host)
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 20);
    return clean || host;
  } catch {
    return '';
  }
}

export default function CreateLinkDemo() {
  const [step, setStep] = useState<'input' | 'created'>('input');
  const [url, setUrl] = useState('');
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);

  useEffect(() => {
    if (slugEdited) return;
    setSlug(suggestSlug(url));
  }, [url, slugEdited]);

  const canSubmit = url.trim().length > 0 && slug.trim().length > 0;

  const reset = () => {
    setStep('input');
    setUrl('');
    setSlug('');
    setTitle('');
    setSlugEdited(false);
  };

  return (
    <div className="grid items-start" style={{ gridTemplateColumns: '1fr 420px', gap: 48 }}>
      {/* LEFT: context + examples */}
      <div>
        <div
          className="inline-block uppercase font-semibold mb-4"
          style={{
            padding: '6px 14px',
            background: 'var(--accent-soft)',
            color: 'var(--accent-deep)',
            borderRadius: 999,
            fontSize: 12,
            letterSpacing: '0.02em',
          }}
        >
          Create your first go/ link
        </div>
        <h3
          className="font-display font-extrabold text-ink m-0 mb-4"
          style={{ fontSize: 40, letterSpacing: '-0.02em', lineHeight: 1.1 }}
        >
          Turn any ugly URL
          <br />
          into a tidy go/ link.
        </h3>
        <p className="text-base text-ink-soft leading-[1.6] m-0 mb-7">
          This is exactly what the Chrome extension popup looks like when you click the Tidy Go Links icon. Paste a URL and we'll auto-suggest a memorable slug.
        </p>
        <div className="font-semibold mb-2.5" style={{ fontSize: 13, color: 'var(--color-ink-soft)' }}>
          Try a URL like:
        </div>
        <div className="flex flex-col" style={{ gap: 8 }}>
          {EXAMPLES.map(ex => (
            <button
              key={ex.url}
              onClick={() => {
                setUrl(ex.url);
                setSlug(ex.slug);
                setTitle(ex.title);
                setSlugEdited(true);
                setStep('input');
              }}
              className="flex items-center bg-surface cursor-pointer text-left transition-colors duration-150"
              style={{
                gap: 12,
                padding: '10px 14px',
                border: '1px solid var(--color-hairline)',
                borderRadius: 14,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-solid)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-hairline)'; }}
            >
              <div className="text-ink-muted" style={{ fontSize: 16 }}>🔗</div>
              <div className="flex-1 min-w-0">
                <div
                  className="font-mono text-ink overflow-hidden text-ellipsis whitespace-nowrap"
                  style={{ fontSize: 12 }}
                >
                  {ex.url}
                </div>
                <div className="text-ink-soft mt-0.5" style={{ fontSize: 11 }}>
                  {ex.hint}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* RIGHT: extension popup */}
      <div className="relative">
        <div
          className="absolute"
          style={{
            top: -12,
            right: 40,
            width: 0,
            height: 0,
            borderLeft: '10px solid transparent',
            borderRight: '10px solid transparent',
            borderBottom: '12px solid var(--color-surface)',
            filter: 'drop-shadow(0 -1px 0 var(--color-hairline))',
            zIndex: 2,
          }}
        />
        <div
          className="bg-surface overflow-hidden w-full"
          style={{
            borderRadius: 14,
            border: '1px solid var(--color-hairline)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          <div
            className="flex items-center bg-bg-alt"
            style={{
              gap: 10,
              padding: '14px 16px',
              borderBottom: '1px solid var(--color-hairline)',
            }}
          >
            <div
              className="flex items-center justify-center font-mono font-bold"
              style={{
                width: 26,
                height: 26,
                borderRadius: 8,
                background: 'var(--accent-soft)',
                color: 'var(--accent-deep)',
                fontSize: 11,
              }}
            >
              go/
            </div>
            <span className="font-bold text-ink" style={{ fontSize: 13 }}>Tidy Go Links</span>
            <span className="ml-auto text-ink-muted" style={{ fontSize: 11 }}>
              acme.com workspace
            </span>
          </div>

          {step === 'input' && (
            <div style={{ padding: 20 }}>
              <Field label="Destination URL">
                <input
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://..."
                  style={inputStyle}
                />
              </Field>

              <Field label="Your go/ link">
                <div
                  className="flex items-stretch overflow-hidden bg-surface"
                  style={{ border: '1px solid var(--color-hairline-strong)', borderRadius: 14 }}
                >
                  <div
                    className="flex items-center font-mono font-semibold"
                    style={{
                      padding: '11px 12px',
                      background: 'var(--accent-soft)',
                      color: 'var(--accent-deep)',
                      fontSize: 14,
                    }}
                  >
                    go/
                  </div>
                  <input
                    value={slug}
                    onChange={e => {
                      setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'));
                      setSlugEdited(true);
                    }}
                    placeholder="roadmap"
                    style={{
                      ...inputStyle,
                      border: 'none',
                      borderRadius: 0,
                      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                      fontSize: 14,
                      fontWeight: 600,
                      padding: '11px 12px',
                    }}
                  />
                </div>
                {slug && !slug.startsWith('-') && (
                  <div className="text-ink-soft mt-1.5" style={{ fontSize: 11 }}>
                    ✓{' '}
                    <code
                      className="font-mono font-semibold"
                      style={{ color: 'var(--accent-deep)' }}
                    >
                      go/{slug}
                    </code>{' '}
                    is available
                  </div>
                )}
              </Field>

              <Field
                label={
                  <span>
                    Title{' '}
                    <span style={{ color: 'var(--color-ink-faint)', fontWeight: 400 }}>(optional)</span>
                  </span>
                }
              >
                <input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Q4 Product Roadmap"
                  style={inputStyle}
                />
              </Field>

              <div
                className="flex bg-bg-alt text-ink-soft leading-[1.5] mt-2 mb-4"
                style={{
                  padding: '10px 12px',
                  borderRadius: 14,
                  fontSize: 12,
                  gap: 8,
                }}
              >
                <span>💡</span>
                <span>
                  Your whole team can use this link the moment you save it. No sharing needed.
                </span>
              </div>

              <C2Button
                variant="accent"
                size="lg"
                className="w-full"
                style={{ opacity: canSubmit ? 1 : 0.5, pointerEvents: canSubmit ? 'auto' : 'none' }}
                onClick={() => canSubmit && setStep('created')}
              >
                Save go/ link
              </C2Button>
            </div>
          )}

          {step === 'created' && (
            <div
              className="text-center"
              style={{ padding: '32px 24px', animation: 'c2fadeSlide 400ms ease-out' }}
            >
              <div
                className="rounded-full flex items-center justify-center mx-auto"
                style={{
                  width: 60,
                  height: 60,
                  background: 'var(--accent-soft)',
                  color: 'var(--accent-deep)',
                  fontSize: 28,
                  marginBottom: 16,
                }}
              >
                ✓
              </div>
              <div
                className="font-display font-extrabold text-ink mb-1.5"
                style={{ fontSize: 22, letterSpacing: '-0.01em' }}
              >
                Your go/ link is live!
              </div>
              <p className="text-ink-soft m-0 leading-[1.55] mb-5" style={{ fontSize: 13 }}>
                Share{' '}
                <code
                  className="font-mono font-semibold"
                  style={{ color: 'var(--accent-deep)' }}
                >
                  go/{slug}
                </code>{' '}
                with anyone at your company — it'll work instantly.
              </p>
              <div
                className="flex items-center bg-bg-alt font-mono font-semibold text-ink mb-5"
                style={{
                  padding: '14px 16px',
                  borderRadius: 14,
                  fontSize: 14,
                  gap: 10,
                  justifyContent: 'center',
                  border: '1px solid var(--color-hairline)',
                }}
              >
                <span style={{ color: 'var(--accent-deep)' }}>go/</span>
                <span>{slug}</span>
                <div className="ml-auto flex" style={{ gap: 6 }}>
                  <button style={copyBtnStyle} title="Copy">⎘</button>
                  <button style={copyBtnStyle} title="Open">↗</button>
                </div>
              </div>
              <button
                onClick={reset}
                className="font-medium cursor-pointer"
                style={{
                  background: 'none',
                  border: '1px solid var(--color-hairline-strong)',
                  padding: '9px 18px',
                  borderRadius: 999,
                  fontSize: 13,
                  color: 'var(--color-ink)',
                }}
              >
                ↻ Create another
              </button>
            </div>
          )}

          <div
            className="flex justify-between text-ink-muted"
            style={{
              padding: '10px 16px',
              borderTop: '1px solid var(--color-hairline)',
              background: 'var(--color-surface-alt)',
              fontSize: 11,
            }}
          >
            <span>⚡ Cmd+Shift+L to open anywhere</span>
            <span>47 links · acme.com</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mb-3.5">
      <label
        className="block font-semibold text-ink mb-1.5"
        style={{ fontSize: 12 }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
